import urls from "global/constants/UrlConstants";
import { Workspace, WorkspaceApiResponse } from "types/workspace";
import { getCallParams, makeCall } from "utils/service";

export const fetchWorkspaces = async (
  includeArchived: boolean = false,
): Promise<Workspace[]> => {
  const url = `${urls.WORKSPACE_FETCH}?includeArchived=${includeArchived}`;
  try {
    const callParams = await getCallParams("GET");
    const response: Workspace[] = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * The caller's effective permissions in the active workspace, for UI gating.
 * {scoped: is a workspace active, admin: bypass, permissions: the caller's permission keys}.
 */
export const fetchMyWorkspacePermissions = async (): Promise<{
  scoped: boolean;
  admin: boolean;
  permissions: string[];
}> => {
  // Errors propagate rather than resolving with a default here: the caller (WorkspaceSwitcher)
  // needs to see the failure to decide fail-open vs fail-closed based on whether a workspace is
  // currently active — swallowing it here would make that decision unreachable.
  try {
    const callParams = await getCallParams("GET");
    const response: any = await makeCall(urls.WORKSPACE_MY_PERMISSIONS, callParams);
    return {
      scoped: Boolean(response?.scoped),
      admin: Boolean(response?.admin),
      permissions: Array.isArray(response?.permissions) ? response.permissions : [],
    };
  } catch (error: any) {
    throw error;
  }
};

/** The current user's own workspaces (membership-based) — for the general switcher. */
export const fetchMyWorkspaces = async (): Promise<Workspace[]> => {
  try {
    const callParams = await getCallParams("GET");
    const response: Workspace[] = await makeCall(urls.WORKSPACE_MINE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const createWorkspace = async (
  name: string,
): Promise<WorkspaceApiResponse> => {
  try {
    const callParams = await getCallParams("POST", { name });
    const response: WorkspaceApiResponse = await makeCall(
      urls.WORKSPACE_ADD,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const renameWorkspace = async (
  id: string,
  name: string,
): Promise<WorkspaceApiResponse> => {
  try {
    const callParams = await getCallParams("PUT", { name });
    const response: WorkspaceApiResponse = await makeCall(
      `${urls.WORKSPACE_UPDATE}/${id}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const archiveWorkspace = async (
  id: string,
): Promise<WorkspaceApiResponse> => {
  try {
    const callParams = await getCallParams("POST");
    const response: WorkspaceApiResponse = await makeCall(
      `${urls.WORKSPACE_ARCHIVE}/${id}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const unarchiveWorkspace = async (
  id: string,
): Promise<WorkspaceApiResponse> => {
  try {
    const callParams = await getCallParams("POST");
    const response: WorkspaceApiResponse = await makeCall(
      `${urls.WORKSPACE_UNARCHIVE}/${id}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};
